<template>
  <div class="smm-report">
    <MenuBar />
    <div class="report-content">
      <div class="header">
        <h1>Social Report Uploader</h1>
      </div>

      <!-- Platform Selection Section -->
      <div class="platform-section">
        <h2>Select Platform</h2>
        <p class="section-subtitle">Choose a social media platform to analyze</p>
        <div class="platforms-grid">
          <div
            v-for="platform in platforms"
            :key="platform.id"
            class="platform-card"
            :class="{ selected: selectedPlatform === platform.id }"
            @click="selectPlatform(platform)"
          >
            <div class="platform-icon">{{ platform.icon }}</div>
            <div class="platform-name">{{ platform.name }}</div>
          </div>
        </div>
      </div>

      <!-- Upload Section (Only visible when platform is selected) -->
      <div v-if="selectedPlatform" class="upload-section">
        <div class="section-header">
          <h2>Upload Analytics</h2>
          <span class="platform-badge">{{ getSelectedPlatformName() }}</span>
        </div>
        
        <div class="form-group">
          <div class="date-inputs">
            <div class="input-field">
              <label for="startDate">Start Date</label>
              <input 
                type="date" 
                id="startDate" 
                v-model="startDate"
                :disabled="uploading"
              />
            </div>
            <div class="input-field">
              <label for="endDate">End Date</label>
              <input 
                type="date" 
                id="endDate" 
                v-model="endDate"
                :disabled="uploading"
              />
            </div>
          </div>
        </div>

        <div
          class="drop-area"
          @dragover.prevent
          @drop.prevent="handleDrop"
        >
          <p>Drag & drop a CSV file here, or click to select</p>
          <input
            type="file"
            ref="fileInput"
            @change="handleFileChange"
            accept=".csv"
            style="display:none"
          />
          <button @click="$refs.fileInput.click()" :disabled="uploading">
            Select CSV
          </button>
        </div>

        <!-- Progress Bar -->
        <div v-if="uploading" class="progress-container">
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: progress + '%' }"></div>
          </div>
          <p class="progress-text">Uploading... {{ progress }}%</p>
        </div>

      </div>

      <!-- Summary Statistics -->
      <div v-if="analysisData && Object.keys(summaryStats).length > 0" class="summary-section">
        <h2>Summary Report</h2>
        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-label">Total Posts</div>
            <div class="stat-value">{{ summaryStats.total_posts || 0 }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Total Views</div>
            <div class="stat-value">{{ (summaryStats.total_views || 0).toLocaleString() }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Total Reaches</div>
            <div class="stat-value">{{ (summaryStats.total_reaches || 0).toLocaleString() }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Total Reactions</div>
            <div class="stat-value">{{ (summaryStats.total_reactions || 0).toLocaleString() }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Total Comments</div>
            <div class="stat-value">{{ (summaryStats.total_comments || 0).toLocaleString() }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Total Shares</div>
            <div class="stat-value">{{ (summaryStats.total_shares || 0).toLocaleString() }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Total Clicks</div>
            <div class="stat-value">{{ (summaryStats.total_clicks || 0).toLocaleString() }}</div>
          </div>
        </div>
      </div>

      <!-- Datewise Report -->
      <div v-if="analysisData && analysisData.datewise && analysisData.datewise.length > 0" class="datewise-section">
        <h2>Datewise Analytics</h2>

        <!-- Datewise Filters -->
        <div class="datewise-filters">
          <div class="input-field">
            <label>Filter by Date</label>
            <input type="date" v-model="filterDate" placeholder="All dates" />
          </div>
          <div class="input-field">
            <label>Filter by Page Name</label>
            <select v-model="filterPageName">
              <option value="">All pages</option>
              <option v-for="page in availablePages" :key="page" :value="page">{{ page }}</option>
            </select>
          </div>
          <div class="input-field" style="align-self: flex-end;">
            <button class="clear-filter-btn" @click="filterDate = ''; filterPageName = ''">Clear Filters</button>
          </div>
        </div>

        <div class="table-container">
          <table class="analytics-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Page Name</th>
                <th>Post Type</th>
                <th>Posts</th>
                <th>Views</th>
                <th>Reaches</th>
                <th>Reactions</th>
                <th>Comments</th>
                <th>Shares</th>
                <th>Clicks</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in filteredDatewiseRows" :key="idx">
                <td>{{ row.date }}</td>
                <td>{{ row.page_name }}</td>
                <td><span class="post-type-badge" :class="row.post_type">{{ row.post_type }}</span></td>
                <td>{{ row.total_posts }}</td>
                <td>{{ row.total_views.toLocaleString() }}</td>
                <td>{{ row.total_reaches.toLocaleString() }}</td>
                <td>{{ row.total_reactions.toLocaleString() }}</td>
                <td>{{ row.total_comments.toLocaleString() }}</td>
                <td>{{ row.total_shares.toLocaleString() }}</td>
                <td>{{ row.total_clicks.toLocaleString() }}</td>
              </tr>
              <tr v-if="filteredDatewiseRows.length === 0">
                <td colspan="10" style="text-align:center; padding: 20px; color: #999;">No data matches the selected filters.</td>
              </tr>
            </tbody>
            <tfoot v-if="filteredDatewiseRows.length > 0">
              <tr class="totals-row">
                <td colspan="3"><strong>Total ({{ filteredDatewiseRows.length }} rows)</strong></td>
                <td><strong>{{ filteredTotals.total_posts.toLocaleString() }}</strong></td>
                <td><strong>{{ filteredTotals.total_views.toLocaleString() }}</strong></td>
                <td><strong>{{ filteredTotals.total_reaches.toLocaleString() }}</strong></td>
                <td><strong>{{ filteredTotals.total_reactions.toLocaleString() }}</strong></td>
                <td><strong>{{ filteredTotals.total_comments.toLocaleString() }}</strong></td>
                <td><strong>{{ filteredTotals.total_shares.toLocaleString() }}</strong></td>
                <td><strong>{{ filteredTotals.total_clicks.toLocaleString() }}</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <!-- Graph Section with Filters -->
      <div v-if="analysisData && analysisData.datewise && analysisData.datewise.length > 0" class="graph-section">
        <h2>📊 Analytics Graph</h2>
        
        <!-- Filters -->
        <div class="graph-filters">
          <div class="filter-group">
            <label>📝 Post Types:</label>
            <div class="multi-select">
              <button
                v-for="postType in availablePostTypes"
                :key="postType"
                class="filter-btn"
                :class="{ active: selectedPostTypes.includes(postType) }"
                @click="togglePostType(postType)"
              >
                {{ postType }}
              </button>
            </div>
          </div>

          <div class="filter-group">
            <label>📄 Pages:</label>
            <div class="multi-select">
              <button
                v-for="page in availablePages"
                :key="page"
                class="filter-btn"
                :class="{ active: selectedPages.includes(page) }"
                @click="togglePage(page)"
              >
                {{ page }}
              </button>
            </div>
          </div>

          <div class="filter-group">
            <label>📈 Metrics:</label>
            <select v-model="selectedMetric" class="metric-select">
              <option value="total_views">Total Views</option>
              <option value="total_reaches">Total Reaches</option>
              <option value="total_reactions">Total Reactions</option>
              <option value="total_comments">Total Comments</option>
              <option value="total_shares">Total Shares</option>
              <option value="total_clicks">Total Clicks</option>
              <option value="total_posts">Total Posts</option>
            </select>
          </div>
        </div>

        <!-- Chart Container -->
        <div class="chart-container">
          <canvas ref="analyticsChart"></canvas>
        </div>

        <!-- Legend -->
        <div class="chart-legend">
          <div v-for="item in chartLegend" :key="item.label" class="legend-item">
            <span class="legend-color" :style="{ backgroundColor: item.color }"></span>
            <span class="legend-label">{{ item.label }}</span>
          </div>
        </div>
      </div>

      <!-- CSV Preview Table -->
      <div v-if="csvData.length > 0" class="preview-section">
        <h2>Social Media Report Analysis</h2>
        <div class="table-container">
          <table class="csv-table">
            <thead>
              <tr>
                <th>Page name</th>
                <th>Title</th>
                <th>Publish time</th>
                <th>Permalink</th>
                <th>Post type</th>
                <th>Views</th>
                <th>Total clicks</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in csvData.slice(0, 10)" :key="idx">
                <td>{{ row['Page name'] || '-' }}</td>
                <td>{{ row['Title'] || '-' }}</td>
                <td>{{ row['Publish time'] || '-' }}</td>
                <td><a :href="row['Permalink']" target="_blank" v-if="row['Permalink']">Link</a><span v-else>-</span></td>
                <td>{{ row['Post type'] || '-' }}</td>
                <td>{{ row['Views'] || '-' }}</td>
                <td>{{ row['Clicks'] || '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="row-count">Preview: {{ Math.min(10, csvData.length) }}/{{ csvData.length }} rows</p>
      </div>
    </div>
  </div>
</template>

<script>
import api from '../api';
import MenuBar from './MenuBar.vue';

export default {
  name: 'SMMReport',
  components: {
    MenuBar
  },
  data() {
    return {
      platforms: [
        { id: 'facebook', name: 'Facebook', icon: '👍', color: '#1877F2' },
        { id: 'tiktok', name: 'TikTok', icon: '🎵', color: '#000000' },
        { id: 'twitter', name: 'X (Twitter)', icon: '𝕏', color: '#000000' },
        { id: 'instagram', name: 'Instagram', icon: '📷', color: '#E4405F' },
        { id: 'linkedin', name: 'LinkedIn', icon: '💼', color: '#0A66C2' },
        { id: 'youtube', name: 'YouTube', icon: '▶️', color: '#FF0000' }
      ],
      selectedPlatform: null,
      uploading: false,
      progress: 0,
      csvData: [],
      csvHeaders: [],
      fileName: '',
      startDate: '',
      endDate: '',
      analysisData: null,
      summaryStats: {},
      // Graph properties
      selectedPostTypes: [],
      selectedPages: [],
      selectedMetric: 'total_views',
      chartLegend: [],
      chart: null,
      availablePostTypes: [],
      availablePages: [],
      filterDate: '',
      filterPageName: ''
    };
  },
  computed: {
    filteredDatewiseRows() {
      if (!this.analysisData || !this.analysisData.datewise) return [];
      return this.analysisData.datewise.filter(row => {
        const dateMatch = !this.filterDate || row.date === this.filterDate;
        const pageMatch = !this.filterPageName || row.page_name === this.filterPageName;
        return dateMatch && pageMatch;
      });
    },
    filteredTotals() {
      const rows = this.filteredDatewiseRows;
      return {
        total_posts:     rows.reduce((s, r) => s + (r.total_posts     || 0), 0),
        total_views:     rows.reduce((s, r) => s + (r.total_views     || 0), 0),
        total_reaches:   rows.reduce((s, r) => s + (r.total_reaches   || 0), 0),
        total_reactions: rows.reduce((s, r) => s + (r.total_reactions || 0), 0),
        total_comments:  rows.reduce((s, r) => s + (r.total_comments  || 0), 0),
        total_shares:    rows.reduce((s, r) => s + (r.total_shares    || 0), 0),
        total_clicks:    rows.reduce((s, r) => s + (r.total_clicks    || 0), 0),
      };
    }
  },
  methods: {
    selectPlatform(platform) {
      this.selectedPlatform = platform.id;
    },
    getSelectedPlatformName() {
      const platform = this.platforms.find(p => p.id === this.selectedPlatform);
      return platform ? platform.name : '';
    },
    handleDrop(e) {
      const files = e.dataTransfer.files;
      if (files.length) {
        this.uploadFile(files[0]);
      }
    },
    handleFileChange(e) {
      const file = e.target.files[0];
      if (file) {
        this.uploadFile(file);
      }
    },
    async uploadFile(file) {
      // Validate file type
      if (!file.name.endsWith('.csv')) {
        this.showStatus('Please upload a valid CSV file', 'error');
        return;
      }

      // Validate dates
      if (!this.startDate || !this.endDate) {
        this.showStatus('Please select both start and end dates', 'error');
        return;
      }

      if (this.startDate > this.endDate) {
        this.showStatus('Start date cannot be after end date', 'error');
        return;
      }

      this.uploading = true;
      this.progress = 0;
      this.csvData = [];
      this.csvHeaders = [];
      this.fileName = file.name;

      // Simulate progress
      const progressInterval = setInterval(() => {
        if (this.progress < 90) {
          this.progress += Math.random() * 30;
        }
      }, 300);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('start_date', this.startDate);
      formData.append('end_date', this.endDate);
      formData.append('platform', this.selectedPlatform);

      try {
        // Parse CSV locally for preview
        this.parseCSVLocally(file);

        // Send to backend
        const res = await api.post('/upload-csv', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        // Store analysis data
        if (res.data.analysis) {
          this.analysisData = res.data.analysis;
          this.summaryStats = res.data.analysis.summary || {};
          // Initialize graph with the new data
          this.initializeGraphData();
        }

        this.progress = 100;
        this.showStatus('✓ CSV uploaded successfully!', 'success');
      } catch (err) {
        clearInterval(progressInterval);
        if (err.response && err.response.status === 401) {
          this.$router.push('/login');
        } else {
          const errorMsg = (err.response && err.response.data && err.response.data.message) || 'Upload failed. Please try again.';
          this.showStatus(errorMsg, 'error');
          this.progress = 0;
        }
      } finally {
        clearInterval(progressInterval);
        setTimeout(() => {
          this.uploading = false;
          this.progress = 0;
        }, 500);
      }
    },
    parseCSVLocally(file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const csv = e.target.result;
        const lines = csv.trim().split('\n');

        if (lines.length === 0) {
          this.showStatus('CSV file is empty', 'error');
          return;
        }

        // Parse headers
        this.csvHeaders = lines[0].split(',').map(h => h.trim());

        // Parse data rows
        this.csvData = lines.slice(1).map(line => {
          const values = line.split(',').map(v => v.trim());
          const obj = {};
          this.csvHeaders.forEach((header, idx) => {
            obj[header] = values[idx] || '';
          });
          return obj;
        });
      };
      reader.readAsText(file);
    },
    showStatus(message, type) {
      this.$notify(message, type || 'info');
    },
    togglePostType(postType) {
      const index = this.selectedPostTypes.indexOf(postType);
      if (index > -1) {
        this.selectedPostTypes.splice(index, 1);
      } else {
        this.selectedPostTypes.push(postType);
      }
      this.updateChart();
    },
    togglePage(page) {
      const index = this.selectedPages.indexOf(page);
      if (index > -1) {
        this.selectedPages.splice(index, 1);
      } else {
        this.selectedPages.push(page);
      }
      this.updateChart();
    },
    initializeGraphData() {
      if (!this.analysisData || !this.analysisData.datewise) return;

      // Extract unique post types and pages
      const postTypes = new Set();
      const pages = new Set();

      this.analysisData.datewise.forEach(row => {
        postTypes.add(row.post_type);
        pages.add(row.page_name);
      });

      this.availablePostTypes = Array.from(postTypes);
      this.availablePages = Array.from(pages);

      // Select all by default
      this.selectedPostTypes = [...this.availablePostTypes];
      this.selectedPages = [...this.availablePages];

      // Initialize chart after Vue updates the DOM
      this.$nextTick(() => {
        this.updateChart();
      });
    },
    updateChart() {
      if (!this.analysisData || !this.analysisData.datewise) return;

      // Filter data based on selections
      const filteredData = this.analysisData.datewise.filter(row =>
        this.selectedPostTypes.includes(row.post_type) &&
        this.selectedPages.includes(row.page_name)
      );

      // Group data by date
      const groupedByDate = {};
      filteredData.forEach(row => {
        if (!groupedByDate[row.date]) {
          groupedByDate[row.date] = 0;
        }
        groupedByDate[row.date] += row[this.selectedMetric];
      });

      // Sort dates
      const dates = Object.keys(groupedByDate).sort();
      const values = dates.map(date => groupedByDate[date]);

      // Wait for DOM to update
      this.$nextTick(() => {
        const ctx = this.$refs.analyticsChart;
        if (!ctx) return;

        // Clear canvas
        const canvas = ctx;
        const parent = canvas.parentElement;
        const newCanvas = document.createElement('canvas');
        parent.replaceChild(newCanvas, canvas);
        this.$refs.analyticsChart = newCanvas;

        // Draw simple line chart using canvas context
        const chart = this.drawChart(newCanvas, dates, values);
      });

      // Update legend
      this.chartLegend = [
        { label: `Post Types: ${this.selectedPostTypes.join(', ')}`, color: '#3498db' },
        { label: `Pages: ${this.selectedPages.join(', ')}`, color: '#e74c3c' }
      ];
    },
    drawChart(canvas, dates, values) {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Set canvas size
      canvas.width = canvas.parentElement.offsetWidth - 40;
      canvas.height = 400;

      const padding = 50;
      const chartWidth = canvas.width - 2 * padding;
      const chartHeight = canvas.height - 2 * padding;

      // Clear canvas
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      if (dates.length === 0) return;

      // Find min and max values
      const maxValue = Math.max(...values, 0);
      const minValue = 0;
      const valueRange = maxValue - minValue || 1;

      // Draw grid lines and labels
      ctx.strokeStyle = '#e0e0e0';
      ctx.fillStyle = '#666';
      ctx.font = '12px Arial';
      ctx.textAlign = 'right';

      for (let i = 0; i <= 5; i++) {
        const y = padding + (chartHeight / 5) * i;
        const value = Math.round(maxValue - (valueRange / 5) * i);

        ctx.strokeStyle = '#e0e0e0';
        ctx.beginPath();
        ctx.moveTo(padding, y);
        ctx.lineTo(canvas.width - padding, y);
        ctx.stroke();

        ctx.fillStyle = '#666';
        ctx.textAlign = 'right';
        ctx.fillText(value.toLocaleString(), padding - 10, y + 5);
      }

      // Draw X-axis labels
      ctx.textAlign = 'center';
      ctx.fillStyle = '#666';
      dates.forEach((date, idx) => {
        const x = padding + (chartWidth / dates.length) * idx + (chartWidth / dates.length) / 2;
        ctx.fillText(date, x, canvas.height - padding + 20);
      });

      // Draw chart line and points
      ctx.strokeStyle = '#3498db';
      ctx.lineWidth = 3;
      ctx.fillStyle = 'rgba(52, 152, 219, 0.1)';
      ctx.beginPath();

      const points = values.map((value, idx) => {
        const x = padding + (chartWidth / dates.length) * idx + (chartWidth / dates.length) / 2;
        const y = padding + chartHeight - ((value - minValue) / valueRange) * chartHeight;
        return { x, y };
      });

      // Draw area under line
      ctx.moveTo(points[0].x, canvas.height - padding);
      points.forEach(point => ctx.lineTo(point.x, point.y));
      ctx.lineTo(points[points.length - 1].x, canvas.height - padding);
      ctx.fill();

      // Draw line
      ctx.strokeStyle = '#3498db';
      ctx.lineWidth = 3;
      ctx.beginPath();
      points.forEach((point, idx) => {
        if (idx === 0) ctx.moveTo(point.x, point.y);
        else ctx.lineTo(point.x, point.y);
      });
      ctx.stroke();

      // Draw points
      ctx.fillStyle = '#3498db';
      points.forEach(point => {
        ctx.beginPath();
        ctx.arc(point.x, point.y, 5, 0, 2 * Math.PI);
        ctx.fill();
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 2;
        ctx.stroke();
      });

      // Draw axes
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(padding, padding);
      ctx.lineTo(padding, canvas.height - padding);
      ctx.lineTo(canvas.width - padding, canvas.height - padding);
      ctx.stroke();

      // Draw title
      ctx.fillStyle = '#2c3e50';
      ctx.font = 'bold 16px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(this.getMetricLabel(this.selectedMetric), canvas.width / 2, 30);
    },
    getMetricLabel(metric) {
      const labels = {
        total_views: 'Total Views',
        total_reaches: 'Total Reaches',
        total_reactions: 'Total Reactions',
        total_comments: 'Total Comments',
        total_shares: 'Total Shares',
        total_clicks: 'Total Clicks',
        total_posts: 'Total Posts'
      };
      return labels[metric] || metric;
    }
  }
};
</script>

<style scoped>
.smm-report {
  display: flex;
  min-height: 100vh;
  background: #f5f5f5;
}

.report-content {
  flex: 1;
  margin-left: 250px;
  padding: 20px;
  overflow-y: auto;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.header h1 {
  margin: 0;
  font-size: 28px;
  color: #2c3e50;
}

/* Platform Selection Section */
.platform-section {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 32px;
  border-radius: 12px;
  margin-bottom: 32px;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.2);
}

.platform-section h2 {
  margin: 0 0 8px 0;
  color: white;
  font-size: 24px;
  font-weight: 700;
}

.section-subtitle {
  margin: 0 0 20px 0;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
}

.platforms-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 16px;
}

.platform-card {
  background: white;
  border: 3px solid transparent;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.platform-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
}

.platform-card.selected {
  border-color: #667eea;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.3);
}

.platform-icon {
  font-size: 48px;
  line-height: 1;
}

.platform-name {
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
}

.platform-card.selected .platform-name {
  color: #667eea;
  font-weight: 700;
}

/* Upload Section Header */
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.section-header h2 {
  margin: 0;
  color: #2c3e50;
  font-size: 20px;
}

.platform-badge {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.upload-section {
  background: white;
  padding: 24px;
  border-radius: 12px;
  margin-bottom: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.form-group {
  margin-bottom: 20px;
}

.date-inputs {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.input-field {
  flex: 1;
  min-width: 180px;
}

.input-field label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
}

.input-field input[type="date"] {
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
  transition: border-color 0.3s ease;
}

.input-field input[type="date"]:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
}

.input-field input[type="date"]:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}

/* Summary Statistics */
.summary-section {
  background: white;
  padding: 24px;
  border-radius: 8px;
  margin-bottom: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.summary-section h2 {
  margin: 0 0 24px 0;
  color: #2c3e50;
  font-size: 22px;
  border-bottom: 2px solid #3498db;
  padding-bottom: 12px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 16px;
}

.stat-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4);
}

.stat-label {
  font-size: 12px;
  font-weight: 600;
  opacity: 0.9;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  word-break: break-word;
}

/* Datewise Filters */
.datewise-filters {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.datewise-filters .input-field {
  flex: 1;
  min-width: 180px;
}

.datewise-filters .input-field label {
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  color: #2c3e50;
  font-size: 13px;
}

.datewise-filters .input-field input[type="date"],
.datewise-filters .input-field select {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}

.clear-filter-btn {
  padding: 8px 16px;
  background: #e74c3c;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  width: 100%;
}

.clear-filter-btn:hover {
  background: #c0392b;
}

/* Datewise Section */
.datewise-section {
  background: white;
  padding: 24px;
  border-radius: 8px;
  margin-bottom: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.datewise-section h2 {
  margin: 0 0 20px 0;
  color: #2c3e50;
  font-size: 22px;
  border-bottom: 2px solid #3498db;
  padding-bottom: 12px;
}

.analytics-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.analytics-table thead {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  position: sticky;
  top: 0;
}

.analytics-table th {
  padding: 14px;
  text-align: left;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.5px;
}

.analytics-table td {
  padding: 12px 14px;
  border-bottom: 1px solid #ecf0f1;
}

.totals-row {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.totals-row td {
  padding: 12px 14px;
  font-size: 13px;
}

.post-type-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.post-type-badge.photos {
  background: #e3f2fd;
  color: #1565c0;
}

.post-type-badge.links {
  background: #f3e5f5;
  color: #6a1b9a;
}

.analytics-table tbody tr:hover {
  background: #f8f9fa;
}

.analytics-table tbody tr:nth-child(even) {
  background: #fafbfc;
}

.drop-area {
  border: 2px dashed #aaa;
  padding: 40px;
  margin-bottom: 20px;
  cursor: pointer;
  background: white;
  border-radius: 8px;
  max-width: 500px;
  text-align: center;
  transition: all 0.3s ease;
}

.drop-area:hover {
  border-color: #3498db;
  background: #f9f9f9;
}

.drop-area p {
  margin: 0 0 15px 0;
  color: #666;
  font-size: 16px;
}

.drop-area button {
  background: #3498db;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.3s ease;
}

.drop-area button:hover:not(:disabled) {
  background: #2980b9;
}

.drop-area button:disabled {
  background: #95a5a6;
  cursor: not-allowed;
}

/* Progress Bar */
.progress-container {
  background: white;
  padding: 20px;
  border-radius: 8px;
  max-width: 500px;
  margin-bottom: 20px;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #ecf0f1;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 10px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #3498db, #2980b9);
  transition: width 0.3s ease;
  border-radius: 10px;
}

.progress-text {
  margin: 0;
  font-size: 14px;
  color: #7f8c8d;
  text-align: center;
}

/* Graph Section */
.graph-section {
  background: white;
  padding: 24px;
  border-radius: 8px;
  margin-bottom: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.graph-section h2 {
  margin: 0 0 20px 0;
  color: #2c3e50;
  font-size: 22px;
  border-bottom: 2px solid #3498db;
  padding-bottom: 12px;
}

.graph-filters {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.filter-group label {
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.multi-select {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.filter-btn {
  padding: 8px 16px;
  background: white;
  border: 2px solid #ddd;
  border-radius: 20px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  transition: all 0.3s ease;
}

.filter-btn:hover {
  border-color: #3498db;
  color: #3498db;
}

.filter-btn.active {
  background: #3498db;
  border-color: #3498db;
  color: white;
}

.metric-select {
  padding: 10px 12px;
  border: 2px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
  color: #2c3e50;
  background: white;
  cursor: pointer;
  transition: border-color 0.3s ease;
}

.metric-select:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
}

.chart-container {
  position: relative;
  width: 100%;
  height: 400px;
  margin-bottom: 20px;
  padding: 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 8px;
}

.chart-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-top: 16px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #2c3e50;
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  display: inline-block;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* CSV Preview Table */
.preview-section {
  background: white;
  padding: 20px;
  border-radius: 8px;
  max-width: 100%;
}

.preview-section h2 {
  margin: 0 0 20px 0;
  color: #2c3e50;
  font-size: 20px;
}

.table-container {
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.csv-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.csv-table thead {
  background: #ecf0f1;
  position: sticky;
  top: 0;
}

.csv-table th {
  padding: 12px;
  text-align: left;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 2px solid #bdc3c7;
}

.csv-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #ecf0f1;
  word-break: break-word;
}

.csv-table tbody tr:hover {
  background: #f9f9f9;
}

.csv-table tbody tr:nth-child(even) {
  background: #fafafa;
}

.row-count {
  margin-top: 16px;
  color: #7f8c8d;
  font-size: 14px;
  text-align: right;
}

/* Responsive: Tablet */
@media (max-width: 768px) {
  .report-content {
    margin-left: 200px;
    padding: 15px;
  }

  .platform-section {
    padding: 20px;
  }

  .platform-section h2 {
    font-size: 20px;
  }

  .platforms-grid {
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
    gap: 12px;
  }

  .platform-card {
    padding: 16px;
  }

  .platform-icon {
    font-size: 36px;
  }

  .platform-name {
    font-size: 12px;
  }

  .platform-badge {
    font-size: 11px;
  }

  .stats-grid {
    grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
    gap: 12px;
  }

  .stat-card {
    padding: 16px;
  }

  .stat-value {
    font-size: 18px;
  }

  .analytics-table {
    font-size: 11px;
  }

  .analytics-table th,
  .analytics-table td {
    padding: 10px 8px;
  }

  .header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .header h1 {
    font-size: 24px;
  }

  .drop-area {
    padding: 30px;
    max-width: 100%;
  }

  .preview-section {
    max-width: 100%;
  }

  .csv-table {
    font-size: 12px;
  }

  .csv-table th,
  .csv-table td {
    padding: 8px;
  }

  .graph-filters {
    grid-template-columns: 1fr;
  }

  .chart-container {
    height: 350px;
  }

  .filter-btn {
    padding: 6px 12px;
    font-size: 11px;
  }

  .metric-select {
    width: 100%;
  }
}

/* Responsive: Mobile */
@media (max-width: 480px) {
  .smm-report {
    flex-direction: column;
  }

  .report-content {
    margin-left: 0;
    padding: 12px;
  }

  .platform-section {
    padding: 16px;
    margin-bottom: 20px;
  }

  .platform-section h2 {
    font-size: 18px;
  }

  .section-subtitle {
    font-size: 12px;
  }

  .platforms-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }

  .platform-card {
    padding: 12px;
  }

  .platform-icon {
    font-size: 28px;
  }

  .platform-name {
    font-size: 11px;
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .platform-badge {
    align-self: flex-start;
  }

  .date-inputs {
    flex-direction: column;
    gap: 12px;
  }

  .input-field {
    min-width: 100%;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .stat-card {
    padding: 12px;
  }

  .stat-label {
    font-size: 10px;
  }

  .stat-value {
    font-size: 16px;
  }

  .analytics-table {
    font-size: 10px;
  }

  .analytics-table th,
  .analytics-table td {
    padding: 8px 6px;
  }

  .header {
    margin-bottom: 20px;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  .header h1 {
    font-size: 20px;
  }

  .drop-area {
    padding: 20px;
    max-width: 100%;
  }

  .drop-area p {
    font-size: 14px;
  }

  .drop-area button {
    width: 100%;
    padding: 12px;
  }

  .progress-container {
    max-width: 100%;
  }

  .preview-section {
    padding: 12px;
  }

  .csv-table {
    font-size: 11px;
  }

  .csv-table th,
  .csv-table td {
    padding: 6px;
  }

  .table-container {
    -webkit-overflow-scrolling: touch;
  }

  .graph-section {
    padding: 16px;
  }

  .graph-filters {
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 12px;
  }

  .filter-group label {
    font-size: 12px;
  }

  .filter-btn {
    padding: 6px 10px;
    font-size: 10px;
  }

  .metric-select {
    width: 100%;
    padding: 8px;
    font-size: 12px;
  }

  .chart-container {
    height: 300px;
    padding: 12px;
  }

  .chart-legend {
    flex-direction: column;
    gap: 12px;
    padding: 12px;
  }

  .legend-item {
    font-size: 12px;
  }
}
</style>
